import { useCallback, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useSearchParams } from "react-router"
import { Warning } from "@phosphor-icons/react"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
} from "@/components/ui/dialog"
import { FaroInsightsWrapper } from "@/grafana-faro"
import { validateUrl } from "@/lib/validate-url"

const PdfLinkWarning = () => {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const [isOpen, setIsOpen] = useState(true)

  const originalUri = searchParams.get("originalUri")
  const urlValidation = useMemo(() => validateUrl(originalUri), [originalUri])

  const handleCancel = useCallback(() => {
    // If opened in a new tab (no history to go back to), close the tab
    // Otherwise, go back to the previous page
    if (window.history.length <= 1) {
      window.close()
    } else {
      setIsOpen(false)
      window.history.back()
    }
  }, [])

  const handleProceed = useCallback(() => {
    if (urlValidation.isValid) {
      FaroInsightsWrapper.pushEvent("pdf_external_link_proceeded", {
        originalUri: urlValidation.url,
      })
      window.location.href = urlValidation.url
    }
  }, [urlValidation])

  if (!urlValidation.isValid) {
    const errorMessage = urlValidation.error === "unsafeScheme" ? t("pdfLink.unsafeScheme") : t("pdfLink.invalidUrl")

    return (
      <div className="flex h-screen flex-col items-center justify-center">
        <p className="text-destructive text-lg">{errorMessage}</p>
        <Button variant="secondary" className="mt-4" onClick={handleCancel}>
          {t("pdfLink.goBack")}
        </Button>
      </div>
    )
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleCancel}>
      <DialogPortal>
        <DialogOverlay />
        <DialogContent variant="small" data-testid="pdf-link-warning-dialog">
          <DialogHeader>
            <DialogTitle>
              <div className="text-warning flex items-center gap-2">
                <Warning className="size-6" />
                {t("pdfLink.title")}
              </div>
            </DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-4">
                <p>{t("pdfLink.message")}</p>
                <div className="bg-muted rounded-md p-3">
                  <p className="text-muted-foreground text-sm font-medium">{t("pdfLink.urlLabel")}</p>
                  <p className="text-warning text-sm break-all italic" data-testid="pdf-link-preview-url">
                    {urlValidation.url}
                  </p>
                </div>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="secondary" onClick={handleCancel} data-testid="pdf-link-cancel-button">
              {t("pdfLink.cancel")}
            </Button>
            <Button variant="primary" onClick={handleProceed} data-testid="pdf-link-proceed-button">
              {t("pdfLink.proceed")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  )
}

export { PdfLinkWarning }
